import {AxiosResponse} from "axios";
import {newBackendAPI} from "services/apiConsumer";
import {QueryPagination} from "types/GenericTypes";
import {TasksByGroups} from "views/Tasks/components/TaskDetails/TaskDetails.types";
import {
  Locations,
  TaskGroupsResponse,
} from "views/Tasks/components/TasksProject/TasksProjectTypes";
import {
  TasksOfProjectDataType,
  ProjectDataPostType,
  TasksUnrelatedToProjects,
  AllFieldsFromProcess,
  TasksDataType,
} from "views/Tasks/types/Tasks.types";

type TasksToBind = {
  idTaskGroup: number;
  tasks: Array<number>;
};

export const getAllProjects = async (
  queryPagination: QueryPagination,
  filter?: {operation: string; modality: string},
): Promise<AxiosResponse<TaskGroupsResponse>> => {
  const params = {
    page: queryPagination.page,
    limit: queryPagination.limit,
    operation: filter?.operation,
    modality: filter?.modality,
  };

  return await newBackendAPI.get(`/task-groups`, {params});
};

export const getTasksByProjectId = async (
  id: number,
  page: number,
  total: number,
  filters?: {[key: string]: string},
  isGeneratedSeparately?: boolean,
): Promise<AxiosResponse<TasksOfProjectDataType>> => {
  const params = {isGeneratedSeparately, page, limit: total, filter: filters};
  return await newBackendAPI.get(`/task-groups/${id}`, {params});
};

export const updateATask = async (data: Partial<TasksDataType>, id: number) => {
  return await newBackendAPI.patch(`/tasks/${id}?onlyHeader=true`, data);
};

export const getTaskRoles = async () => {
  return await newBackendAPI.get("/task-roles");
};

export const postNewProject = async (newProject: ProjectDataPostType) => {
  return await newBackendAPI.post("/task-groups", newProject);
};

export const updateProject = async (
  projectId: number,
  changesInProject: Partial<ProjectDataPostType>,
) => {
  return await newBackendAPI.patch(
    `/task-groups/${projectId}`,
    changesInProject,
  );
};

export const getFollowUpTemplates = async (pagination?: QueryPagination) => {
  const params = {...pagination};
  return await newBackendAPI.get("/template-follow-ups", {params});
};

export const getFollowUpTemplateById = async (id: number) => {
  return await newBackendAPI.get(`/template-follow-ups/${id}`);
};

export const getMessageTemplates = async (pagination?: QueryPagination) => {
  const params = {...pagination};
  return await newBackendAPI.get("/template-messages", {params});
};

export const getMessageTemplateById = async (id: number) => {
  return await newBackendAPI.get(`/template-messages/${id}`);
};

export const getTasksWithoutLinkedProjects = async (): Promise<
  AxiosResponse<TasksUnrelatedToProjects[]>
> => {
  return await newBackendAPI.get(`/tasks/?groupTask=null`);
};

export const bindTasksToAProject = async (
  tasks: TasksToBind,
): Promise<AxiosResponse<{message: string}>> => {
  return await newBackendAPI.patch(`/bind-tasks`, tasks);
};

export const getProcessDateFields = async (): Promise<
  AxiosResponse<AllFieldsFromProcess>
> => {
  return await newBackendAPI.get("/all-fields-from-process?type=DateTime");
};

export const getAllFieldsFromProcess = async (
  type?: string,
  search?: string,
  idTaskGroup?: number,
  source?: "task" | "watchedfields",
): Promise<AxiosResponse<AllFieldsFromProcess>> => {
  const params = {
    type: type,
    search: search,
    idTaskGroup: idTaskGroup,
    source: source,
  };
  return await newBackendAPI.get(`/all-fields-from-process`, {params});
};

export const addNewTaskRule = async (data) => {
  return await newBackendAPI.post(`/tasks`, data);
};

export const updateTaskRule = async (data, id: number) => {
  return await newBackendAPI.patch(`/tasks/${id}`, data);
};

export const getTask = async (id: number) => {
  return await newBackendAPI.get(`/tasks/${id}`);
};

export const deleteSpecialRule = async (id: number) => {
  return await newBackendAPI.delete(`/special-rules/${id}`);
};

export const getAllTasksByGroupId = async (
  id: number,
): Promise<AxiosResponse<TasksByGroups>> => {
  return await newBackendAPI.get(`tasks/?page=1&limit=100&idTaskGroup=${id}`);
};

/**
 *
 * @param type "AEROPORTO" | "PORTO" | "OUTROS", separado por virgula ('AEROPORTO,PORTO')
 */
export const getPorts = async (
  page = 1,
  limit = 50,
  search: string | undefined = "",
  type?: string,
): Promise<AxiosResponse<Locations>> => {
  const params = {
    name: search,
    page,
    limit,
    type,
  };
  return await newBackendAPI.get(`/ports/`, {params});
};

export const GetMessageGroups = async (pagination?: QueryPagination) => {
  const params = {...pagination};

  return await newBackendAPI.get("/send-message-groups", {params});
};

export const GetFileGroups = async (pagination?: QueryPagination) => {
  const params = {...pagination};

  return await newBackendAPI.get("/group-files", {params});
};

export const DeleteTaskDependency = async (dependencyId: number) => {
  return await newBackendAPI.delete(`/task-dependencies/${dependencyId}`);
};
